import React from "react";
import {
  FaExternalLinkSquareAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const ExperienceCard = ({ experience, isOpen, setOpenExperience }) => {
  const isExpanded = isOpen === experience.company;

  return (
    <div
      className={`w-full cursor-pointer border-t border-zinc-200 py-4 text-left text-base transition-colors duration-150 dark:border-zinc-800 ${
        isExpanded
          ? "bg-zinc-50/70 dark:bg-zinc-900/30"
          : "hover:bg-zinc-50/70 dark:hover:bg-zinc-900/30"
      }`}
      onClick={() => setOpenExperience(isExpanded ? null : experience.company)}
    >
      <div className="flex w-full flex-row items-start justify-between px-1">
        {/* Company Logo */}
        <div className="mr-4 shrink-0">
          <img
            src={experience.image || `${process.env.PUBLIC_URL}/placeholder.png`}
            alt={`${experience.company} logo`}
            className="h-11 w-11 rounded-md border border-zinc-200 bg-white object-cover dark:border-zinc-800"
          />
        </div>

        {/* Experience Info */}
        <div className="flex min-w-0 flex-grow flex-col">
          <div className="flex w-full items-start justify-between gap-3">
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center flex-wrap gap-2">
                {experience.link ? (
                  <a
                    href={experience.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-words text-base font-semibold text-zinc-950 hover:underline dark:text-white"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {experience.company}
                  </a>
                ) : (
                  <b className="break-words text-base text-zinc-950 dark:text-white">{experience.company}</b>
                )}
                {experience.badges?.map((badge, idx) => (
                  <span 
                    key={idx}
                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${badge.color}20`, color: badge.color, border: `1px solid ${badge.color}40` }}
                  >
                    {badge.text}
                  </span>
                ))}
              </div>
              <p className="mt-0.5 break-words text-sm text-zinc-700 dark:text-zinc-300">
                {experience.position} • {experience.date}
              </p>
              {experience.tech ? (
                <p className="mt-1 break-words text-sm text-zinc-500 dark:text-zinc-400">
                  {experience.tech}
                </p>
              ) : null}
            </div>

            {/* Icons */}
            <div className="flex items-center space-x-2 shrink-0">
              {experience.link && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={experience.link}
                  className="p-1 text-zinc-500 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Open ${experience.company}`}
                >
                  <FaExternalLinkSquareAlt size={18} />
                </a>
              )}
              <button
                className="p-1 text-zinc-500 transition-colors duration-150 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenExperience(isExpanded ? null : experience.company);
                }}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${experience.company}`}
              >
                {isExpanded ? (
                  <FaChevronUp size={18} />
                ) : (
                  <FaChevronDown size={18} />
                )}
              </button>
            </div>
          </div>

          {/* Expandable Section */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isExpanded
                ? "mt-3 max-h-[640px] opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            <ul className="list-disc space-y-1.5 pl-5 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
              {experience.bulletPoints.map((point, idx) => (
                <li className="self-start" key={idx}>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceCard;
